import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  X, 
  Eye, 
  Download, 
  Share2, 
  Calendar, 
  MapPin, 
  User, 
  Hash, 
  Globe, 
  FileText,
  ExternalLink,
  Copy,
  CheckCircle
} from 'lucide-react';

const ViewNoticeModal = ({ isOpen, onClose, notice, isOwner = false }) => {
  const [copied, setCopied] = useState(false);

  if (!isOpen || !notice) return null;

  const handleCopyLink = async () => {
    const link = `${window.location.origin}/public-notices/${notice.id}`;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy link:', error);
    }
  };

  const handleDownloadPDF = async () => {
    if (!notice) return;

    try {
      // Call the backend PDF generation endpoint
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/notices/public/${notice.id}/download-pdf`);
      
      if (response.ok) {
        const blob = await response.blob();
        
        // Create download link
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        
        // Get filename from response headers or create default
        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = 'Public_Notice.pdf';
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
          if (filenameMatch) {
            filename = filenameMatch[1];
          }
        } else {
          // Create filename from notice title
          const safeTitle = notice.title.replace(/[^a-zA-Z0-9]/g, '_').substring(0, 50);
          filename = `${safeTitle}_Public_Notice.pdf`;
        }
        
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        
        // Clean up
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
      } else {
        console.error('Failed to download PDF:', response.status);
        alert('Failed to download PDF. Please try again.');
      }
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert('Failed to download PDF. Please try again.');
    }
  };

  const getNoticeTypeIcon = (type) => {
    switch (type) {
      case 'Affidavit of Status':
        return <FileText className="w-4 h-4" />;
      default:
        return <FileText className="w-4 h-4" />;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-start space-x-3">
              {getNoticeTypeIcon(notice.notice_type)}
              <div>
                <h2 className="text-2xl font-semibold text-gray-900 mb-2">{notice.title}</h2>
                <div className="flex items-center space-x-2 mb-3">
                  <Badge variant="outline" className="text-xs">
                    {notice.notice_type}
                  </Badge>
                  <Badge variant="outline" className="text-xs">
                    {notice.jurisdiction}
                  </Badge>
                  {notice.is_featured && (
                    <Badge className="bg-yellow-100 text-yellow-700 text-xs">
                      Featured
                    </Badge>
                  )}
                  {isOwner && (
                    <Badge className="bg-blue-100 text-blue-700 text-xs">
                      Your Notice
                    </Badge>
                  )}
                </div>
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="w-5 h-5" />
            </Button>
          </div>

          {/* Metadata */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
              <div className="flex items-center space-x-2">
                <Calendar className="w-4 h-4 text-gray-500" />
                <div>
                  <div className="font-medium text-gray-700">Published</div>
                  <div className="text-gray-600">{new Date(notice.created_at).toLocaleDateString()}</div>
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <Eye className="w-4 h-4 text-gray-500" />
                <div>
                  <div className="font-medium text-gray-700">Views</div>
                  <div className="text-gray-600">{notice.view_count}</div>
                </div>
              </div>

              {notice.user_name && (
                <div className="flex items-center space-x-2">
                  <User className="w-4 h-4 text-gray-500" />
                  <div>
                    <div className="font-medium text-gray-700">Published by</div>
                    <div className="text-gray-600">{notice.user_name}</div>
                  </div>
                </div>
              )}

              {notice.location && (
                <div className="flex items-center space-x-2">
                  <MapPin className="w-4 h-4 text-gray-500" />
                  <div>
                    <div className="font-medium text-gray-700">Location</div>
                    <div className="text-gray-600">{notice.location}</div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Hash Proof */}
          {notice.hash_proof && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <div className="flex items-start space-x-3">
                <Hash className="w-5 h-5 text-blue-600 mt-0.5" />
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-blue-800 mb-1">Sovereign Proof Hash</h3>
                  <p className="text-xs text-blue-700 mb-2">
                    This cryptographic hash provides immutable proof of publication timestamp and content integrity.
                  </p>
                  <div className="bg-white border border-blue-200 rounded p-2 font-mono text-xs text-blue-900 break-all">
                    {notice.hash_proof}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Content */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Notice Content</h3>
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="prose max-w-none">
                <div className="whitespace-pre-wrap text-gray-800 leading-relaxed">
                  {notice.body_text}
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between pt-4 border-t border-gray-200">
            <div className="text-sm text-gray-500">
              <Globe className="w-4 h-4 inline mr-1" />
              Public Record #{notice.id.substring(0, 8)}
            </div>
            
            <div className="flex space-x-3">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleCopyLink}
                className="flex items-center space-x-2"
              >
                {copied ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                <span>{copied ? 'Copied!' : 'Copy Link'}</span>
              </Button>
              
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleDownloadPDF}
                className="flex items-center space-x-2"
              >
                <Download className="w-4 h-4" />
                <span>Download PDF</span>
              </Button>
              
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => {
                  onClose();
                  setTimeout(() => {
                    window.location.href = `/public-notices/${notice.id}`;
                  }, 100);
                }}
                className="flex items-center space-x-2"
              >
                <ExternalLink className="w-4 h-4" />
                <span>Public View</span>
              </Button>
              
              <Button onClick={onClose}>
                Close
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewNoticeModal;